import { I18nTranslator } from '@strength/i18n';
import axios from 'axios';
import { fileTypeFromBuffer, FileTypeResult } from 'file-type';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { environment } from '../../../../environment';
import { useToast } from '../../../hooks/useToast';

type FILE_CATEGORY = 'image' | 'video';

// `file-type`'s recommended minimum bytes for magic-number detection.
const FILE_TYPE_SNIFF_BYTES = 4100;

const ACCEPTED_FILE_TYPES = {
  image: ['image/jpeg', 'image/png', 'image/gif'],
  video: ['video/mp4', 'video/webm'],
  file: ['application/pdf'],
};

const ACCEPTED_EXTENSIONS = {
  image: ['jpg', 'png', 'gif'],
  video: ['mp4', 'webm'],
  file: ['pdf'],
};

interface useUploadFileProps {
  onComplete: (fileUrl: string) => void | Promise<void>;
}

export const useUploadFile = ({
  onComplete,
}: useUploadFileProps): [
  (files: FileList, filePrefix: string, fileCategory: FILE_CATEGORY) => object,
  boolean,
] => {
  const [fileUploading, setFileUploading] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation();
  const uploadFile = async (
    files: FileList,
    filePrefix: string,
    fileCategory: FILE_CATEGORY,
  ) => {
    setFileUploading(true);

    // validate file
    let validatedFile;
    try {
      validatedFile = await getValidatedFile(files, fileCategory, t);
    } catch (error) {
      const msg =
        error instanceof Error
          ? error.message
          : t('couldntValidateFile', { fileName: validatedFile });
      toast({
        title: t('error'),
        description: msg,
        severity: 'error',
      });
      setFileUploading(false);
      return;
    }

    // Detect the type from the header bytes only, so large videos aren't read into memory.
    const headerBytes = new Uint8Array(
      await validatedFile.slice(0, FILE_TYPE_SNIFF_BYTES).arrayBuffer(),
    );
    const fileType = await fileTypeFromBuffer(headerBytes);
    // Throw error if the file type isn't recognized
    if (
      !fileType ||
      !ACCEPTED_FILE_TYPES[fileCategory].includes(fileType.mime) ||
      !ACCEPTED_EXTENSIONS[fileCategory].includes(fileType.ext)
    ) {
      toast({
        title: t('error'),
        description: t('unsupportedFileType'),
        severity: 'error',
      });
      setFileUploading(false);
      return;
    }

    try {
      const res = await axios.get(`${environment.VITE_API_URL}/files`, {
        withCredentials: true,
        params: {
          filePrefix,
          fileType: `.${fileType.ext}`,
        },
      });

      if (res.status === 200) {
        const toUpload = await uploadFileToAWS({
          resData: res.data,
          fileType,
          file: validatedFile,
        });
        await onComplete(decodeURI(toUpload));
      } else {
        toast({
          title: t('error'),
          description: t('failedToUploadFile'),
          severity: 'error',
        });
      }
    } catch (e) {
      toast({
        title: t('error'),
        description: t('failedToUploadFile'),
        severity: 'error',
      });
    }
    setFileUploading(false);
  };

  return [uploadFile, fileUploading];
};

interface UploadFileProps {
  resData: { signedRequestUrl: string; url: string };
  fileType: FileTypeResult;
  file: File;
}

/**
 * Uploads the raw file to S3 via the presigned PUT URL and returns its URL.
 * No Content-Encoding is set — a non-identity encoding breaks range requests
 * and video playback.
 */
export const uploadFileToAWS = async ({
  resData,
  fileType,
  file,
}: UploadFileProps) => {
  const { signedRequestUrl, url } = resData;
  await axios.put(signedRequestUrl, file, {
    headers: {
      'Content-Type': fileType.mime,
    },
  });
  return url;
};

/**
 * @throws Error if the file is invalid
 */
const getValidatedFile = async (
  file: FileList,
  fileCategory: FILE_CATEGORY,
  t: I18nTranslator,
): Promise<File> => {
  if (fileCategory === 'image') {
    return getValidatedImageFile(file, t);
  } else if (fileCategory === 'video') {
    return getValidatedVideoFile(file, t);
  }
  throw new Error(t('invalidFileCategory', { fileCategory }));
};

const getValidatedImageFile = async (
  files: FileList,
  t: I18nTranslator,
): Promise<File> => {
  if (files.length !== 1) {
    throw new Error(t('onlyOneImageCanBeUploaded'));
  }

  const file = files[0];
  if (!file) {
    throw new Error(t('noFileSelected'));
  }

  if (!file.type.startsWith('image')) {
    throw new Error(t('notAnImageFile', { fileName: file.name }));
  }

  // validate size
  const img = new Image();
  img.src = URL.createObjectURL(file);
  await img.decode();

  if (img.width > 800 || img.height > 400) {
    throw new Error(t('imageTooLarge', { fileName: file.name }));
  }

  return file;
};

const getValidatedVideoFile = (files: FileList, t: I18nTranslator): File => {
  if (files.length !== 1) {
    throw new Error(t('onlyOneVideoCanBeUploaded'));
  }
  const validatedFile = Array.from(files).filter((file) => {
    if (!file.type.startsWith('video')) {
      throw new Error(t('notAVideoFile', { fileName: file.name }));
    }

    // validate file size, max 2gb
    if (file.size > 2 * 1024 * 1024 * 1024) {
      throw new Error(t('videoTooLarge', { fileName: file.name }));
    }

    return true;
  })[0];

  if (!validatedFile) {
    throw new Error(t('unableToValidateFile'));
  }
  return validatedFile;
};
